// Prompt definitions: the answer widgets shared by every stage (rebuild.md §9.5, §9.8).
// Pure data. `check.ts` grades a response; `solve.ts` produces a correct response for the solver bot.

/**
 * Rich text: plain English with light markup rendered by <RichText>:
 *   {CuSO4.5H2O} chemistry (rendered by <Chem>) · **bold** · _italic_ · blank line = paragraph.
 */
export type Rich = string;

export type Tier = 'open' | 'rationed' | 'permit' | 'blackout';
export const TIERS: readonly Tier[] = ['open', 'rationed', 'permit', 'blackout'];

/** Per-tier grading parameters (from stage/tiers.ts). */
export interface TierParams {
  tier: Tier;
  /** Relative numeric tolerance (0.05 = ±5%); null = graded by significant figures. */
  tolerance: number | null;
  sigFigs: 'ignore' | 'advise' | 'enforce';
  unitPicker: boolean;
}

export interface Option {
  id: string;
  label: Rich;
  /** Optional swatch colour (always accompanied by the label text). */
  swatch?: string;
}

export interface Item {
  id: string;
  label: Rich;
}

/** A predictable wrong answer → targeted feedback (§4.7). */
export interface NumericDiagnosis {
  id: string;
  /** The wrong value this rule recognises (graded with the tier tolerance). */
  value: number;
  /** Optional unit the wrong value is expressed in. */
  unit?: string;
  feedback: Rich;
  /** The wrong value comes from the instrument's `values` (e.g. the titre not halved, from the player's own mean). */
  from?: string;
}

export interface NumericAnswer {
  /** Produced by the kernel at content load; never hand-typed when a kernel function exists. */
  value: number;
  unit: string;
  /** Required at Permit/Blackout. */
  sigFigs?: number;
  /** Overrides the tier's relative tolerance. */
  tolerance?: Partial<Record<Tier, number>>;
  /** The number printed in rebuild.md, cross-checked against `value` by the content truth tests. */
  spec?: number;
  /**
   * Graded against a value the instrument computes from the player's own data (its `values` hook), e.g. purity
   * from their mean titre. `value` stays the canonical answer (true end point) for the content checks.
   */
  from?: string;
}

/** Open-tier guided calculator: the formula with blanks; the widget computes the result. */
export interface GuidedCalc {
  /** Shown above the blanks, e.g. "AE = M(product) ÷ M(reactants) × 100%". */
  formula: Rich;
  /** `from`: the field's value comes from the instrument (the player's own data), like NumericAnswer.from. */
  fields: { id: string; label: Rich; value: number; given?: boolean; from?: string }[];
  /** Arithmetic over field ids: + − × ÷ and parentheses, e.g. `a / b * 100`. */
  expression: string;
  /** A predictable wrong number in one blank → targeted feedback (e.g. the wet mass in the yield blank). */
  fieldDiagnose?: { id: string; field: string; value: number; feedback: Rich }[];
}

export type StateSymbol = 's' | 'l' | 'g' | 'aq';

export interface SpeciesTile {
  formula: string;
  side: 'reactant' | 'product';
  state?: StateSymbol;
  /** Other states accepted for this species (water from a combustion: (l) or (g)). */
  accept?: StateSymbol[];
}

export interface FormulaAnswer {
  formula: string;
  /** `ionic`: compared as ion counts; `molecular`: exact composition; `empirical`: same ratio. */
  mode: 'ionic' | 'molecular' | 'empirical';
  ions?: [cationId: string, anionId: string];
}

/** A predictable wrong formula → targeted feedback (compared by composition). */
export interface FormulaDiagnosis {
  id: string;
  formula: string;
  feedback: Rich;
}

/** A skeleton (atoms and which pairs are joined) for the Lewis builder; mirrors chem/lewis LewisSkeleton. */
export interface LewisTarget {
  name: string;
  /** Formula markup for labels, e.g. `CO2`. */
  formula: string;
  atoms: { id: string; el: string; x: number; y: number }[];
  bonds: [string, string][];
  charge?: number;
}

export interface LewisDraft {
  bonds: number[];
  lonePairs: Record<string, number>;
}

export interface NameAnswer {
  name: string;
  alternates?: string[];
  /** Accept the name without Roman numerals (with a note) below this tier. */
  romanNumeralRequiredFrom?: Tier;
}

export type PromptDef =
  | { kind: 'choice'; q: Rich; options: Option[]; answer: string | string[]; diagnose?: Record<string, Rich>; columns?: number }
  | { kind: 'numeric'; q: Rich; answer: NumericAnswer; units?: string[]; diagnose?: NumericDiagnosis[]; calculator?: GuidedCalc }
  | { kind: 'formula'; q: Rich; answer: FormulaAnswer; input: 'keypad' | 'ion-tiles'; tiles?: string[]; showCharges?: boolean; diagnose?: FormulaDiagnosis[] }
  | {
      kind: 'equation';
      q: Rich;
      species: SpeciesTile[];
      answer: number[];
      /** Ask for state symbols (graded). */
      states?: boolean;
      /** Show each species' state symbol, read-only (Open: states pre-filled). */
      statesGiven?: boolean;
      /** Show a live atom tally under the editor. */
      tally?: boolean;
      maxCoefficient?: number;
    }
  /** `tiles`: the Name Builder (§6.10) assembles the name from these on Open; `diagnose`: predictable wrong names. */
  | { kind: 'name'; q: Rich; answer: NameAnswer; tiles?: string[]; diagnose?: { name: string; feedback: Rich }[] }
  | { kind: 'classify'; q: Rich; items: Item[]; bins: Option[]; answer: Record<string, string> }
  | { kind: 'order'; q: Rich; items: Item[]; answer: string[] }
  | { kind: 'match'; q: Rich; left: Item[]; right: Item[]; answer: Record<string, string> }
  | { kind: 'configuration'; q: Rich; answer: { Z: number; charge: number }; form?: 'full' | 'condensed' | 'either' }
  | { kind: 'fields'; q: Rich; fields: { id: string; label: Rich; answer: number | string; unit?: string }[] }
  | { kind: 'label'; q: Rich; slots: { id: string; label: Rich }[]; answer: { formula: FormulaAnswer; name: NameAnswer }; input: 'keypad' | 'ion-tiles'; tiles?: string[]; showCharges?: boolean }
  /** Instrument-built answers (§9.5 `build`). Lewis: any octet-valid arrangement on the given skeleton. */
  | { kind: 'build'; q: Rich; builder: 'lewis'; target: LewisTarget }
  /**
   * A form to verify (§9.5 `checklist`): every field as filed, the corrections a field could take, the planted
   * errors (field → the right correction) and, on Permit, fields that look wrong but are right (`falseFlags`).
   */
  | { kind: 'checklist'; q: Rich; fields: ChecklistField[]; errors: Record<string, string>; falseFlags?: string[]; diagnose?: Record<string, Rich> };

export interface ChecklistField {
  id: string;
  label: Rich;
  /** The value as filed. */
  value: Rich;
  /** Corrections on offer if the field is flagged. */
  fixes?: Option[];
}

export type PromptKind = PromptDef['kind'];

export type Response =
  | { kind: 'choice'; selected: string[] }
  | { kind: 'numeric'; text: string; unit?: string; fields?: Record<string, string> }
  | { kind: 'formula'; text: string }
  | { kind: 'equation'; coefficients: number[]; states?: (string | null)[] }
  | { kind: 'name'; text: string }
  | { kind: 'classify'; placement: Record<string, string> }
  | { kind: 'order'; order: string[] }
  | { kind: 'match'; pairs: Record<string, string> }
  | { kind: 'configuration'; text: string }
  | { kind: 'fields'; values: Record<string, string> }
  | { kind: 'label'; formula: string; name: string }
  | { kind: 'build'; builder: 'lewis'; lewis: LewisDraft }
  /** Flagged fields → the correction chosen (unflagged fields are kept as filed). */
  | { kind: 'checklist'; fixes: Record<string, string> };

export interface Verdict {
  correct: boolean;
  /** Feedback for the player (diagnosis, unit hint, s.f. note). */
  feedback?: Rich;
  /** Machine id of the matched diagnosis, e.g. `unit`, `sigfigs`, `charge`, or a content id. */
  diagnosis?: string;
  /** Advisory note on a correct answer (e.g. s.f. advice on Rationed, "sulphate" spelling). */
  note?: Rich;
  /** Per-item marks for classify/order/match/fields (ids of wrong items). */
  wrongItems?: string[];
  /** Response could not be read (does not count as a mistake). */
  invalid?: boolean;
}
